import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function VerifyEmail() {
  const { verifyEmail } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [status, setStatus] = useState("loading"); // loading | success | error
  const [error, setError] = useState("");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const token = params.get("token");
    if (!token) { setStatus("error"); setError("This verification link is missing its token."); return; }
    (async () => {
      const res = await verifyEmail(token);
      if (res.ok) {
        setStatus("success");
        const wantsSupporter = res.user?.wants_supporter;
        setTimeout(() => {
          if (wantsSupporter) {
            toast.success("Make a gift to unlock your Supporter status and perks.");
            navigate("/supporter");
          } else {
            navigate("/dashboard");
          }
        }, 1800);
      } else {
        setStatus("error");
        setError(res.error);
      }
    })();
  }, [params, verifyEmail, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-bone px-6" data-testid="verify-email-page">
      <div className="w-full max-w-md text-center">
        <Link to="/" className="block"><img src="/logo.png" alt="In His Image Movement Ministries" className="h-24 w-auto object-contain mx-auto" /></Link>

        {status === "loading" && (
          <div className="mt-12" data-testid="verify-loading">
            <Loader2 size={30} className="text-navy animate-spin mx-auto" />
            <p className="font-sans text-charcoal/60 mt-6">Verifying your email…</p>
          </div>
        )}

        {status === "success" && (
          <div className="mt-10" data-testid="verify-success">
            <div className="inline-flex items-center justify-center w-16 h-16 border border-olive/30 bg-olive/10 rounded-full">
              <CheckCircle2 size={28} className="text-olive" strokeWidth={1.5} />
            </div>
            <h1 className="font-serif font-light text-4xl mt-8 tracking-tight">You're verified.</h1>
            <p className="font-sans text-charcoal/60 mt-4">Welcome to the movement. Taking you in…</p>
          </div>
        )}

        {status === "error" && (
          <div className="mt-10" data-testid="verify-error">
            <div className="inline-flex items-center justify-center w-16 h-16 border border-rust/30 bg-rust/10 rounded-full">
              <XCircle size={28} className="text-rust" strokeWidth={1.5} />
            </div>
            <h1 className="font-serif font-light text-4xl mt-8 tracking-tight">Link problem.</h1>
            <p className="font-sans text-charcoal/60 mt-4">{error}</p>
            <p className="font-sans text-sm text-charcoal/60 mt-8">
              <Link to="/login" className="text-olive border-b border-olive/40">Go to sign in</Link> to request a new link.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
